import { useNavigate } from "@tanstack/solid-router";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { For, Show, createEffect, createSignal } from "solid-js";
import { track } from "~/lib/analytics";
import { withBase } from "~/lib/base-path";
import {
  fetchInterests,
  fetchMeetupMe,
  updateMeetupHomeCity,
  updateMemberProfile,
} from "~/lib/api";
import { MEETUP_CITIES } from "~/lib/meetup";

type Step = "welcome" | "identity" | "place" | "tour";

const LATER_KEY = "deb-connect-setup-later";
const RETURN_KEY = "deb-connect-setup-return";

const FEATURES = [
  ["City nights", "Register and show a door QR. Open now."],
  ["Directory", "People at a conference, after you claim a ticket."],
  ["Connections and scan", "Requests and badge scan while that city is live."],
  ["Opportunities", "Roles and collabs with a Connect Pass."],
  ["Tickets", "Your orders, and assigning a seat to someone else."],
] as const;

export function SetupProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = createSignal<Step>("welcome");
  const [displayName, setDisplayName] = createSignal("");
  const [headline, setHeadline] = createSignal("");
  const [company, setCompany] = createSignal("");
  const [jobRole, setJobRole] = createSignal("");
  const [homeCity, setHomeCity] = createSignal("");
  const [selected, setSelected] = createSignal<string[]>([]);
  const [error, setError] = createSignal("");

  const interestsQuery = createQuery(() => ({
    queryKey: ["interests"],
    queryFn: fetchInterests,
    enabled: step() === "place",
  }));
  const meetupMeQuery = createQuery(() => ({
    queryKey: ["meetup-me"],
    queryFn: fetchMeetupMe,
    enabled: step() === "place",
  }));

  createEffect(() => {
    const city = meetupMeQuery.data?.data.home_city;
    if (city && !homeCity()) setHomeCity(city);
  });

  createEffect(() => {
    track("onboarding_step_viewed", { step: step() }, { page: "/setup" });
  });

  const saveIdentity = createMutation(() => ({
    mutationFn: () =>
      updateMemberProfile({
        display_name: displayName().trim(),
        headline: headline().trim() || undefined,
        company: company().trim() || undefined,
        job_role: jobRole().trim() || undefined,
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(["member-profile"], res);
      setError("");
      setStep("place");
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "Could not save your profile."),
  }));

  const savePlace = createMutation(() => ({
    mutationFn: async () => {
      const city = homeCity().trim();
      if (city) await updateMeetupHomeCity(city);
      return updateMemberProfile({ interest_tag_ids: selected() });
    },
    onSuccess: (res) => {
      queryClient.setQueryData(["member-profile"], res);
      queryClient.invalidateQueries({ queryKey: ["meetup-me"] });
      setError("");
      setStep("tour");
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "Could not save your city."),
  }));

  function leave() {
    const stored = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    if (stored && stored.startsWith("/") && !stored.startsWith("/setup")) {
      window.location.assign(withBase(stored));
      return;
    }
    navigate({ to: "/" });
  }

  function later() {
    sessionStorage.setItem(LATER_KEY, "1");
    leave();
  }

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 10 ? [...prev, id] : prev,
    );
  }

  return (
    <section class="card wizard">
      <p class="status-chip">Setup · {stepIndex(step())} of 4</p>

      <Show when={step() === "welcome"}>
        <h1>Your profile is empty</h1>
        <p class="muted">
          Connect knows the email you signed in with. A name, a home city, and a few topics is enough
          for people to recognise you.
        </p>
        <div class="wizard-actions">
          <button type="button" class="btn btn-primary" style={{ width: "100%" }} onClick={() => setStep("identity")}>
            Set up your profile
          </button>
          <button type="button" class="btn btn-ghost" style={{ width: "100%" }} onClick={later}>
            Not now
          </button>
        </div>
      </Show>

      <Show when={step() === "identity"}>
        <h1>Who you are</h1>
        <p class="muted">This is the card other people see. A name is enough to continue.</p>
        <form
          class="wizard-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!displayName().trim()) {
              setError("Add your name to continue.");
              return;
            }
            saveIdentity.mutate();
          }}
        >
          <label class="field">
            <span>Name</span>
            <input
              value={displayName()}
              required
              onInput={(e) => setDisplayName(e.currentTarget.value)}
              placeholder="Your name"
            />
          </label>
          <label class="field">
            <span>Headline</span>
            <input
              value={headline()}
              onInput={(e) => setHeadline(e.currentTarget.value)}
              placeholder="What you work on"
            />
          </label>
          <label class="field">
            <span>Company</span>
            <input
              value={company()}
              onInput={(e) => setCompany(e.currentTarget.value)}
              placeholder="Where you work"
            />
          </label>
          <label class="field">
            <span>Role</span>
            <input
              value={jobRole()}
              onInput={(e) => setJobRole(e.currentTarget.value)}
              placeholder="Job title"
            />
          </label>
          <Show when={error()}>
            <div class="error-box">{error()}</div>
          </Show>
          <div class="wizard-actions">
            <button type="submit" class="btn btn-primary" style={{ width: "100%" }} disabled={saveIdentity.isPending}>
              Continue
            </button>
          </div>
        </form>
      </Show>

      <Show when={step() === "place"}>
        <h1>Where you show up</h1>
        <p class="muted">Home city sorts meetups. Topics help people find you. Both can wait.</p>
        <label class="field">
          <span>Home city</span>
          <select
            value={homeCity()}
            aria-label="Home city"
            onChange={(e) => setHomeCity(e.currentTarget.value)}
          >
            <option value="">Not set</option>
            <For each={MEETUP_CITIES}>{(city) => <option value={city.toLowerCase()}>{city}</option>}</For>
          </select>
        </label>
        <p class="muted">Topics</p>
        <Show when={interestsQuery.isLoading}>
          <p class="muted">Loading topics…</p>
        </Show>
        <Show when={interestsQuery.data}>
          <div class="interest-grid">
            <For each={interestsQuery.data!.data}>
              {(tag) => (
                <button
                  type="button"
                  class={`interest-chip${selected().includes(tag.id) ? " selected" : ""}`}
                  onClick={() => toggle(tag.id)}
                >
                  {tag.label}
                </button>
              )}
            </For>
          </div>
        </Show>
        <Show when={error()}>
          <div class="error-box">{error()}</div>
        </Show>
        <div class="wizard-actions">
          <button
            type="button"
            class="btn btn-primary"
            style={{ width: "100%" }}
            disabled={savePlace.isPending}
            onClick={() => savePlace.mutate()}
          >
            Continue
          </button>
        </div>
      </Show>

      <Show when={step() === "tour"}>
        <h1>What you can do</h1>
        <p class="muted">City nights are open now. Conference tools unlock when you claim a ticket.</p>
        <ul class="event-list">
          <For each={FEATURES}>
            {([title, body]) => (
              <li>
                <strong>{title}</strong>
                <p class="muted">{body}</p>
              </li>
            )}
          </For>
        </ul>
        <div class="wizard-actions">
          <button type="button" class="btn btn-primary" style={{ width: "100%" }} onClick={leave}>
            Enter Connect
          </button>
        </div>
      </Show>
    </section>
  );
}

function stepIndex(step: Step): number {
  if (step === "welcome") return 1;
  if (step === "identity") return 2;
  if (step === "place") return 3;
  return 4;
}

export function rememberSetupReturn(path: string) {
  if (typeof sessionStorage === "undefined") return;
  if (!path.startsWith("/") || path.startsWith("/setup")) return;
  sessionStorage.setItem(RETURN_KEY, path);
}

export function profileSetupDismissed(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(LATER_KEY) === "1";
}
